import React, { useEffect, useState } from 'react'
import {
    Typography,
    Box,
    Button,
    IconButton,
    Snackbar,
    Alert,
    Grid
} from '@mui/material';
import { useDispatch } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';

import { deleteProjectById, modifyProjectSolde } from '../services/database/Projects';
import { updateProjectSolde, deleteProject } from '../redux/reducers/ProjectsSlice';
import { formatNumberToEuroString } from '../utils/FormatToPrice';

import DrawerLayout from '../components/containers/drawerLayout/DrawerLayout';
import ContentContainer from '../components/containers/ContentContainer';
import SplitCard from '../components/cards/SplitCard';
import LoaderCircular from '../components/LoaderCircular'
import DialogModifySolde from '../components/dialogs/DialogModifySolde';
import DialogDeleteProject from '../components/dialogs/DialogDeleteProject';

import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import EuroIcon from '@mui/icons-material/Euro';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const Project = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const { projectId } = useParams();

    const [openDialog, setOpenDialog] = useState(false);
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [openLoaderCircular, setOpenLoaderCircular] = useState(false);

    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [snackbarSeverity, setSnackbarSeverity] = useState('error');
    const [snackbarMessage, setSnackbarMessage] = useState('');

    const allProjects = useSelector(state => state.projects)
    const [project, setProject] = useState(undefined)
    const [moneyToInvest, setMoneyToInvest] = useState(0)

    useEffect(() => {
        if (allProjects.length !== 0) {
            const projectIndex = allProjects.findIndex(
                (project) => project.projectId === projectId
            )

            if (projectIndex !== -1) {
                const projectFind = allProjects[projectIndex];

                setProject(projectFind)

                setMoneyToInvest(
                    projectFind.solde - calculateSoldeMin(projectFind.splits)
                )
            } else {
                navigateToHome()
            }
        }

    }, [allProjects, navigate, projectId])

    const calculateSoldeMin = (splitsArr) => {
        let soldeMin = 0;

        splitsArr.map(split => soldeMin += split.solde)

        return soldeMin;
    }


    const handleOpenSnackBar = (message = 'Ouverture de la SnackBar', severity = 'error') => {
        setOpenSnackbar(true)
        setSnackbarMessage(message)
        setSnackbarSeverity(severity)
    }

    const handleCloseSnackBar = () => {
        setOpenSnackbar(false)
        setSnackbarMessage('')
        setSnackbarSeverity('error')
    }

    const handleModifySolde = (newSolde) => {
        setOpenLoaderCircular(true)
        modifyProjectSolde(projectId, newSolde)
            .then(() => {
                dispatch(updateProjectSolde({ projectId, newSolde }))
                handleOpenSnackBar('Solde modifié avec succès !', 'success')
                setOpenLoaderCircular(false)
            }).catch(err => {
                console.error(err);
                handleOpenSnackBar("Une erreur c'est produite au moment de modifier le solde du projet, merci de réessayer.", 'error')
                setOpenLoaderCircular(false)
            })
    }

    const handleDeleteProject = () => {
        setOpenLoaderCircular(true)
        deleteProjectById(projectId, project.splits)
            .then(() => {
                dispatch(deleteProject({ projectId }))
                navigateToHome()
            }).catch(err => {
                console.error(err);
                setOpenLoaderCircular(false)
                handleOpenSnackBar("Une erreur c'est produite au moment de supprimer le projet, merci de réessayer.", 'error')
            })
    }

    const handleNavigateToModifyProject = () => {
        navigate(`/modifyProject/${projectId}`)
    }

    const handleNavigateToCreateSplit = () => {
        navigate(`/createSplit/${projectId}`)
    }

    const navigateToHome = () => {
        navigate('/')
    }

    if (project === undefined) {
        return (
            <LoaderCircular open={true} />
        )
    }

    return (
        <>
            <LoaderCircular open={openLoaderCircular} />

            <DialogDeleteProject
                open={openDeleteDialog}
                onDialogClose={() => setOpenDeleteDialog(false)}
                onSubmit={() => handleDeleteProject()}
            />

            <DialogModifySolde
                onDialogClose={() => setOpenDialog(false)}
                open={openDialog}
                initialSolde={project.solde}
                onSubmit={newSolde => handleModifySolde(newSolde)}
                minSolde={calculateSoldeMin(project.splits)}
            />

            <Snackbar open={openSnackbar} autoHideDuration={6000} onClose={() => handleCloseSnackBar()}>
                <Alert onClose={() => handleCloseSnackBar()} severity={snackbarSeverity} sx={{ width: '100%' }}>
                    {snackbarMessage}
                </Alert>
            </Snackbar>

            <DrawerLayout projectId={projectId} headerTitle={project.title}>
                <ContentContainer>
                    <IconButton onClick={() => navigateToHome()} color='primary'>
                        <ArrowBackIcon />
                    </IconButton>
                    <Typography
                        variant='h1' sx={{ marginBottom: 4, fontSize: { xs: 40, md: 48 } }}
                        fontWeight='bold'
                    >
                        {project.title}
                    </Typography>

                    <Typography color='GrayText' variant='subtitle1'>
                        {project.description}
                    </Typography>

                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', minWidth: 200, marginTop: 2 }}>
                        <Box sx={{ marginRight: 2 }}>
                            <Typography
                                variant='h2'
                                fontSize={20}
                                fontWeight='bold'
                            >
                                {formatNumberToEuroString(project.solde)} €
                            </Typography>
                            <Typography variant="body2" color="text.secondary" >Solde total</Typography>
                        </Box>

                        <Box sx={{ marginRight: 2 }}>
                            <Typography
                                variant='h2'
                                fontSize={20}
                                color={moneyToInvest < 0 ? 'error' : 'primary'}
                            >
                                {formatNumberToEuroString(moneyToInvest)} €
                            </Typography>
                            <Typography variant="body2" color="text.secondary">A répartir</Typography>
                        </Box>
                    </Box>

                    {moneyToInvest < 0 ?
                        <Alert severity="error" sx={{ marginY: 2, alignSelf: 'flex-start' }}>
                            Attention, solde trop bas !
                            Nous vous conseillons de modifier le solde de votre projet à minimum {calculateSoldeMin(project.splits)} €,
                            ou alors, enlevez en tout {formatNumberToEuroString(Math.abs(moneyToInvest))} € à vos spliits.
                        </Alert>
                        : null
                    }

                    <Box sx={{ display: 'flex', marginTop: 2, marginBottom: 4 }}>
                        <Button
                            onClick={() => handleNavigateToCreateSplit()}
                            sx={{ marginRight: 2 }}
                            variant='contained'
                            startIcon={<AddIcon />}
                        >
                            Spliit
                        </Button>

                        <IconButton onClick={() => setOpenDialog(true)} sx={{ marginRight: 2 }} color='primary'>
                            <EuroIcon />
                        </IconButton>

                        <IconButton onClick={() => handleNavigateToModifyProject()} sx={{ marginRight: 2 }} color='primary'>
                            <EditIcon />
                        </IconButton>

                        <IconButton onClick={() => setOpenDeleteDialog(true)} sx={{ marginRight: 2 }} color='primary'>
                            <DeleteIcon />
                        </IconButton>
                    </Box>

                    {
                        project.splits.length !== 0 ?
                            <Grid container spacing={2}>
                                {
                                    project.splits.map(split => {
                                        return (
                                            <Grid item key={split.splitId}>
                                                <SplitCard split={split} />
                                            </Grid>
                                        )
                                    })
                                }
                            </Grid>
                            :
                            <Typography variant='subtitle2' color="GrayText" sx={{ marginTop: 5 }} fontSize={20} >
                                Cliquer sur
                                <Button
                                    onClick={() => handleNavigateToCreateSplit()}
                                    sx={{ marginX: 1, fontSize: 10 }}
                                    variant='contained'
                                    startIcon={<AddIcon fontSize="small" />}
                                    size='small'
                                >
                                    Spliit
                                </Button>
                                pour créer un spliit.
                            </Typography>
                    }
                </ContentContainer>
            </DrawerLayout>
        </>
    )
}

export default Project;